import React, { Dispatch, FC, ReactElement, SetStateAction, useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useSelector } from 'react-redux';
import { useFetchOnMount } from '@qwealth/qcore';
import { SelectOption, selectUser } from '@qwealth/qdata';
import { getAccessibleCalendars } from 'services/microsoft/graphService';
import { ORGANIZER_LIST_KEY } from 'data/refData/calendar';

interface Props {
  isDisabled?: boolean;
  from: string;
  setFrom: Dispatch<SetStateAction<string>>;
}

const getSavedOrganizers = (): Array<string> => {
  const rawOrganizers = localStorage.getItem(ORGANIZER_LIST_KEY);
  return rawOrganizers ? JSON.parse(rawOrganizers) : [];
};

const FromOrganizer: FC<Props> = ({ isDisabled, from, setFrom }): ReactElement => {
  const user = useSelector(selectUser);
  const calendars = useFetchOnMount(getAccessibleCalendars, [])(true);
  const fromEmails = useMemo(() => {
    const options: Array<SelectOption<string>> = [{ label: user.email, value: user.email }];
    const emails = [user.email];
    calendars
      .filter(({ canEdit }) => canEdit)
      .forEach(({ id, owner }) => {
        const address = owner?.address;
        if (user.email !== address && id && address && !emails.includes(address)) {
          emails.push(address);
          options.push({
            value: address,
            label: address,
          });
        }
      });

    getSavedOrganizers()
      .filter(o => !emails.includes(o))
      .forEach(email => {
        options.push({ label: email, value: email });
      });

    return options;
  }, [user, calendars]);

  const defaultValue = { label: from, value: from };
  const value = fromEmails.find(({ value }) => value === from) || defaultValue;

  return (
    <CreatableSelect
      isDisabled={isDisabled}
      options={fromEmails}
      formatCreateLabel={val => `From email: ${val}`}
      onChange={opt => {
        const email = opt?.value ?? '';
        const savedEmails = getSavedOrganizers();
        if (!savedEmails.includes(email) && email !== user.email) {
          localStorage.setItem(ORGANIZER_LIST_KEY, JSON.stringify([...savedEmails, email]));
        }
        setFrom(email);
      }}
      value={value}
    />
  );
};

export default FromOrganizer;
